import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import {
  ConsultantThread,
  ConsultantMessage,
  ThreadStatus,
  ThreadWithMessages,
} from "@/types/messaging";

async function invoke<T>(action: string, params: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("messaging-api", {
    body: { action, ...params },
  });
  if (error) throw await toEdgeFunctionFailure(error, "Unknown error");
  return data as T;
}

export class MessagingApiClient {
  async listThreads(opts: {
    companyId?: string;
    status?: ThreadStatus;
  }): Promise<{ threads: ConsultantThread[] }> {
    return invoke("listThreads", opts);
  }

  async getThread(threadId: string): Promise<ThreadWithMessages> {
    return invoke("getThread", { threadId });
  }

  async createThread(opts: {
    companyId: string;
    subject?: string;
  }): Promise<{ thread: ConsultantThread }> {
    return invoke("createThread", { ...opts, createdByRole: "consultant" });
  }

  async sendMessage(opts: {
    threadId: string;
    body: string;
    isFromAiDraft?: boolean;
  }): Promise<{ message: ConsultantMessage }> {
    return invoke("sendMessage", { ...opts, senderRole: "consultant" });
  }

  async updateThreadStatus(
    threadId: string,
    status: ThreadStatus,
  ): Promise<{ thread: ConsultantThread }> {
    return invoke("updateThreadStatus", { threadId, status });
  }

  async generateAiReply(threadId: string): Promise<string> {
    const { draft } = await invoke<{ draft: string }>("generateAiReply", { threadId });
    return draft;
  }

  async suggestSubject(threadId: string): Promise<string> {
    const { subject } = await invoke<{ subject: string }>("suggestSubject", { threadId });
    return subject;
  }

  async updateSubject(threadId: string, subject: string): Promise<ConsultantThread> {
    const { thread } = await invoke<{ thread: ConsultantThread }>("updateSubject", { threadId, subject });
    return thread;
  }

  async getAttachmentDownloadUrl(attachmentId: string): Promise<string> {
    const { url } = await invoke<{ url: string }>("getAttachmentDownloadUrl", { attachmentId });
    return url;
  }
}
